// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useSelector } from 'react-redux';
import { Box, useTheme } from '@mui/system';
import { Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import { getOrders } from 'Features/order/orderSelectors';
import { useEffect } from 'react';
import { SetAllAttributeAddOnsSelectedAction, SetIncludeAttributeAction } from 'Features/bundle/bundleActions';
import usePrevious from 'Components/hooks/usePrevious';
import { utilitiesPackagesSubtypes } from 'Utils/constants';
import ParentLayerItem from './ParentLayerItem';
import stylesDef from './styles';
import { Logout, Warning } from '@mui/icons-material';
import LightTooltip from 'Components/Tooltip';

interface LayerSelectProps {
  bundleType: string;
  onAddOnsClick: (selectedList: { item: IDeliverable, checked: boolean, typeFactor3D?: boolean}[]) => void;
  manageAddOns: (deliverableId: number, type: string, typeFactor: number, checked: boolean) => void;
  checkFirstTimeChangeEstimate: (callback: () => void) => void;
}

export const LayerSelect = ({ bundleType, onAddOnsClick, manageAddOns, checkFirstTimeChangeEstimate }: LayerSelectProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { additionalDeliverables, selectedBundle, availableBundles, selectedAdditionals, attributeAddOnsList, allAttributeAddOnsSelected, includeAttribute, attributeAddOnsFactor } = useAppSelector(
    (state) => state.bundle,
  );
  const { imageryRequest } = useAppSelector((state) => state.project.project);
  const previousIncludeAttribute = usePrevious(includeAttribute);

  const { name: bundleName, bundleId, type, subtype } = selectedBundle || {};
  const { activeOrder } = useSelector((state: RootState) => getOrders(state));
  const utilitiesBundle = bundleType === 'utilities';

  const ugAlaCarte = availableBundles.find((b) => b.name === 'Underground A-la-carte');

  const ugAlaCarteBoolean = bundleName === 'Underground A-la-carte';
  const aerialBundleBoolean = bundleName === 'Aerial A-la-carte';
  const roadsAndSidewalkDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Roads & Sidewalks');
  const vegetationAndLandscapeDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Vegetation & Landscape');
  const topographyLayerDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Topography' && d.type === 'layer');
  const administrativeDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Administrative & Annotations');
  const planimetricsDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Planimetrics');
  const measurementsDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Measurements');
  const wallsAndFencesDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Walls & Fences');

  // Utilities Add-ons
  const utilitiesDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Utilities');
  const aerialMiscWiresDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Misc. Wires') : [];
  const aerialBuiltInfrastructureDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Built Infrastructure') : [];

  const utilityPolesDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Utility Poles') : [];
  const wiresAttachmentsDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Wires & Attachments') : [];
  const utilityPoleEquipmentDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Utility Pole - Equipment') : [];
  const guyWiresDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Guy Wire') : [];
  const clearancesDeliverables = utilitiesBundle ? additionalDeliverables.filter((d) => d.subtype === 'Clearances') : [];

  const lowConfidenceDeliverables = additionalDeliverables.filter((d) => d.nearmapConfidence === 'low');

  // In aerial bundle, Available GIS Data are included in the Basemap subtype
  // In underground bundle, Available GIS Data are separate from Basemap subtype
  let basemapDeliverables: IDeliverable[] = [];
  let gisDataDeliverables: IDeliverable[] = [];
  if (subtype === 'Aerial (3D)') {
    basemapDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Basemap' || d.subtype === 'Available GIS Data');
  } else if (utilitiesBundle) {
    basemapDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Basemap');
    gisDataDeliverables = additionalDeliverables.filter((d) => d.subtype === 'Available GIS Data');
  }

  useEffect(() => {
    const allSelected = attributeAddOnsList.length && attributeAddOnsList.every((item) => selectedAdditionals.some((selected) => selected.deliverableId === item));
    dispatch(SetAllAttributeAddOnsSelectedAction(allSelected));
    if (!allSelected) {
      dispatch(SetIncludeAttributeAction(false));
    }
  }, [selectedAdditionals]);

  useEffect(() => {
    if (previousIncludeAttribute !== undefined && previousIncludeAttribute !== includeAttribute) {
      manageAddOns(-1, 'layer', attributeAddOnsFactor, includeAttribute);
    }
  }, [includeAttribute]);

  const handleAttributeSwitch = (event: React.MouseEvent<HTMLElement>, newAlignment: boolean) => {
    checkFirstTimeChangeEstimate(() => {
      dispatch(SetIncludeAttributeAction(newAlignment));
    });
  };

  return (
    <Box sx={bundleName === 'Topo' && styles.unavailableDeliverables}>
      <Box sx={styles.headContainer}>
        <Typography sx={styles.addonsHeading} variant="h3">
          Layers
        </Typography>
        {aerialBundleBoolean && (
          <Box sx={styles.attributeContainer}>
            <Logout fontSize='small' sx={styles.attributeAddOnIcon} />
            <Typography variant="h6">Output:</Typography>
            <LightTooltip
              title={<Typography color="textSecondary"><b>All layers</b> should be enabled to switch the Output.</Typography>}
              placement="top"
              sx={allAttributeAddOnsSelected && styles.hiddenTooltip}
            >
              <ToggleButtonGroup
                value={includeAttribute}
                exclusive
                onChange={handleAttributeSwitch}
                aria-label="attribute toggle"
                disabled={!allAttributeAddOnsSelected}
              >
                <ToggleButton value={false} aria-label="location" sx={styles.toggleButton}>
                  Location
                </ToggleButton>
                <ToggleButton value aria-label="location & dimension" sx={styles.toggleButton}>
                  Location & Dimension
                </ToggleButton>
              </ToggleButtonGroup>
            </LightTooltip>
          </Box>
        )}
      </Box>
      {bundleName === 'Topo' ? (
        <Typography sx={styles.noDeliverableMessage}>
          Please select the 2D + TOPO bundle to add 2D features at checkout
        </Typography>
      ) : <Typography sx={styles.description}>Please select any additional layers you’d like to be drafted in your project.</Typography> }
      {lowConfidenceDeliverables.length > 0 && imageryRequest && (
        <Box sx={styles.lowConfidenceContainer}>
          <Warning color='warning'/>
          <LightTooltip
            title={
              lowConfidenceDeliverables.length > 1
                ? `Nearmap imagery may not support full accuracy for: ${lowConfidenceDeliverables.map((d) => d.name).join(', ')}`
                : `Nearmap imagery may not support full accuracy for: ${lowConfidenceDeliverables[0].name}`
            }
            placement="top"
            arrow
          >
            <Typography
              sx={styles.warningText}
            >
              Nearmap imagery may not support full accuracy for: <b>{lowConfidenceDeliverables.map((d) => d.name).join(', ')}</b>.
            </Typography>
          </LightTooltip>
        </Box>
      )}
      {(bundleId === 4 || bundleId === 5) && (
        <Typography sx={styles.noDeliverableMessage}>
          Please select the 2D + TOPO bundle to add the disabled layers at checkout
        </Typography>
      )}
      {utilityPolesDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Utility Poles
          </Typography>
          <ParentLayerItem
            items={utilityPolesDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {/* Basemap subtype displays in different section in Underground and Aerial. Here for Underground. */}
      {subtype === utilitiesPackagesSubtypes[0] && basemapDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Basemap
          </Typography>
          <ParentLayerItem
            items={basemapDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {utilitiesDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Utilities
          </Typography>
          <ParentLayerItem
            items={utilitiesDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {wiresAttachmentsDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Wires & Attachments
          </Typography>
          <ParentLayerItem
            items={wiresAttachmentsDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {utilityPoleEquipmentDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Utility Pole - Equipment
          </Typography>
          <ParentLayerItem
            items={utilityPoleEquipmentDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}

      {guyWiresDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Guy Wire
          </Typography>
          <ParentLayerItem
            items={guyWiresDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {clearancesDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Clearances
          </Typography>
          <ParentLayerItem
            items={clearancesDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {/* Basemap subtype displays in different section in Underground and Aerial. Here for Aerial. */}
      {subtype === utilitiesPackagesSubtypes[1] && basemapDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Basemap
          </Typography>
          <ParentLayerItem
            items={basemapDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {aerialMiscWiresDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Misc. Wires
          </Typography>
          <ParentLayerItem
            items={aerialMiscWiresDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {aerialBuiltInfrastructureDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Built Infrastructure
          </Typography>
          <ParentLayerItem
            items={aerialBuiltInfrastructureDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => !item.selectable}
          />
        </Box>
      )}
      {gisDataDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Available GIS Data
          </Typography>
          <ParentLayerItem
            items={gisDataDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {roadsAndSidewalkDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Roads & Sidewalks
          </Typography>
          <ParentLayerItem
            items={roadsAndSidewalkDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {vegetationAndLandscapeDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Vegetation & Landscape
          </Typography>
          <ParentLayerItem
            items={vegetationAndLandscapeDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable || (bundleName === '3D Feature Elevation' && !(item.deliverableId === 39))}
          />
        </Box>
      ) }
      {topographyLayerDeliverables.length > 0 && (
        <Box sx={bundleName === '2D' && styles.unavailableDeliverables}>
          <Typography sx={styles.addonsHeading} variant="h4">
            Topography
          </Typography>
          {bundleName === '2D' && (
            <Typography sx={styles.noDeliverableMessage}>
              Please select the 2D + TOPO bundle to add Topography features at checkout
            </Typography>
          )}
          <ParentLayerItem
            items={topographyLayerDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      ) }
      {administrativeDeliverables.length > 0 && (
        <Box sx={(bundleId === 0 || bundleId === 5) && styles.unavailableDeliverables}>
          <Typography sx={styles.addonsHeading} variant="h4">
            Administrative & Annotations
          </Typography>
          {(bundleId === 0 || bundleId === 5) && (
            <Typography sx={styles.noDeliverableMessage}>
              Please select the 2D + TOPO bundle to add Administrative & Annotations features at checkout
            </Typography>
          )}
          <ParentLayerItem
            items={administrativeDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      )}
      {planimetricsDeliverables.length > 0 && (
        <Box sx={bundleId === 4 && styles.unavailableDeliverables}>
          <Typography sx={styles.addonsHeading} variant="h4">
            Planimetrics
          </Typography>
          <ParentLayerItem
            items={planimetricsDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type !== 'utilities' && !item.selectable || (bundleName === '3D Feature Elevation' && !(item.deliverableId === 21))}
          />
        </Box>
      )}
      {measurementsDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Measurements
          </Typography>
          <ParentLayerItem
            items={measurementsDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      ) }
      {wallsAndFencesDeliverables.length > 0 && (
        <Box>
          <Typography sx={styles.addonsHeading} variant="h4">
            Walls & Fences
          </Typography>
          <ParentLayerItem
            items={wallsAndFencesDeliverables}
            activeOrder={activeOrder}
            ugAlaCarte={ugAlaCarte}
            ugAlaCarteBoolean={ugAlaCarteBoolean}
            onAddOnsClick={onAddOnsClick}
            isUnavailable={(item: IDeliverable) => type === 'utilities' && !item.selectable}
          />
        </Box>
      ) }
    </Box>
  );
};

export default LayerSelect;
